import { useEffect, useRef, useState } from 'react';
import { Tag, TagType } from '../../apis/api-client';
import { tagsClient } from '../../apis/client-instances';
import { Autocomplete, Checkbox, Chip, CircularProgress, TextField } from '../ui';

const SEARCH_PAGE_SIZE = 25;
const DEBOUNCE_MS = 250;
const DEFAULT_MIN_WIDTH = 220;

export interface TagAutocompleteProps {
  value: Tag | Tag[] | null;
  onChange: (value: Tag | Tag[] | null) => void;
  multiple?: boolean;
  /** Restrict to a specific tag type (UserTag, CourseTag, …). */
  tagType?: TagType;
  /** Restrict to tags belonging exactly to this workspace. */
  workspaceId?: number;
  /** When workspaceId is set, also include account-wide tags. */
  includeAccountWide?: boolean;
  /** Only return account-wide tags (workspaceId IS NULL). Trumps workspaceId. */
  accountWideOnly?: boolean;
  /** When provided (and non-empty), restrict the picker to ONLY tags
   *  whose IDs are in this list. Used to enforce "child scope must
   *  be a subset of parent scope" — e.g. a group's content tags must
   *  be a subset of its workspace's contentTagIds. Passing an empty
   *  array or `undefined` leaves the picker unrestricted. */
  restrictToIds?: number[];
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: any;
}

/**
 * Lazy-loaded tag picker. Server-side search via /api/Tags/search with name + paging
 * so we never bulk-load every tag in the account.
 *
 * Caller owns the selected Tag objects. For initial hydration from stored IDs use
 * tagsClient.searchTags with the `ids` param.
 */
export default function TagAutocomplete({
  value,
  onChange,
  multiple = false,
  tagType,
  workspaceId,
  includeAccountWide = false,
  accountWideOnly = false,
  restrictToIds,
  label,
  placeholder,
  size = 'small',
  fullWidth = false,
  disabled = false,
  required = false,
  error = false,
  helperText,
  sx,
}: TagAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Tag[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const reqIdRef = useRef(0);

  // Serialize the restrict-to list so the effect's dep array reacts
  // to actual changes instead of identity-only.
  const restrictKey = restrictToIds ? restrictToIds.join(',') : '';

  useEffect(() => {
    if (!open) return;
    // Caller pinned the picker to an explicit (empty) allowlist —
    // there are zero tags to pick from, so we short-circuit.
    if (restrictToIds && restrictToIds.length === 0) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const id = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await tagsClient.searchTags(
          inputValue || undefined,
          tagType,
          workspaceId ?? undefined,
          includeAccountWide,
          accountWideOnly,
          // Pass the allowlist when present so the picker's universe
          // is the parent scope, not every tag in the account.
          restrictToIds && restrictToIds.length > 0 ? restrictToIds : undefined,
          1,
          SEARCH_PAGE_SIZE
        );
        if (id !== reqIdRef.current) return;
        let items = result.items ?? [];
        // Defensive client-side filter: even if the API doesn't honor
        // the `ids` allowlist for some reason, we still hide
        // out-of-scope options so the user can't pick them.
        if (restrictToIds && restrictToIds.length > 0) {
          const allow = new Set(restrictToIds);
          items = items.filter((t) => t.id != null && allow.has(t.id));
        }
        setOptions(items);
      } catch {
        if (id !== reqIdRef.current) return;
        setOptions([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, open, tagType, workspaceId, includeAccountWide, accountWideOnly, restrictKey]);

  const hasSelection = multiple
    ? Array.isArray(value) && value.length > 0
    : value != null;

  return (
    <Autocomplete<Tag, boolean, false, false>
      multiple={multiple}
      // In multi-select mode keep the dropdown open while toggling
      // selections — otherwise picking each tag closes the menu and
      // selecting several is friction-heavy.
      disableCloseOnSelect={multiple}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value as any}
      onChange={(_e, newValue) => onChange(newValue as any)}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(opt, val) => opt.id === (val as Tag)?.id}
      noOptionsText={inputValue ? 'No tags match' : 'Type to search'}
      renderOption={
        multiple
          ? (props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox checked={selected} size="small" sx={{ mr: 1, p: 0.5 }} />
                {option.name}
              </li>
            )
          : undefined
      }
      renderTags={(items, getTagProps) =>
        items.map((option, index) => (
          <Chip
            label={option.name}
            size="small"
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      sx={{
        minWidth: DEFAULT_MIN_WIDTH,
        '& .MuiAutocomplete-clearIndicator': {
          visibility: hasSelection ? 'visible' : 'hidden',
        },
        ...sx,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
